import { useMemo, useState } from 'react'

import { useUnit } from 'effector-react'

import CheckIcon from '@/assets/icons/icon-check.svg'
import CloseIcon from '@/assets/icons/icon-close.svg'
import { Button } from '@/components/ui/button.tsx'
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
} from '@/components/ui/card.tsx'
import { Tooltip } from '@/components/ui/tooltip.tsx'
import { $account } from '@/models/account.ts'
import { DiscountBadge } from '@/modules/campaigns/components/discount-badge.tsx'
import { useCampaigns } from '@/modules/campaigns/state/hooks.ts'
import { classNames } from '@/utils/helpers.ts'

import type { BillingPlan } from '../types'
import { LicenseMode } from '../types'
import { PurchaseLicenseDialog } from './dialogs/purchase-license-dialog.tsx'

interface LicensePackProps {
  licenses: BillingPlan[]
  license: BillingPlan
  mode: LicenseMode
  isRecommend?: boolean
  showDiscount?: boolean
}

export const LicensePack = ({
  license,
  mode,
  isRecommend,
  showDiscount,
}: LicensePackProps) => {
  const account = useUnit($account)
  const inTrial = account != null && account.inTrial
  const [purchaseOpen, setPurchaseOpen] = useState(false)
  const { applyDiscount } = useCampaigns()

  const licensePrice =
    mode === LicenseMode.YEARLY
      ? Math.ceil(license.yearlyPrice / 12)
      : license.monthlyPrice

  const price = useMemo(() => {
    if (showDiscount) {
      return (
        <>
          <div className="flex items-end gap-1 overflow-hidden">
            <h2 className="text-nowrap text-3xl font-semibold">
              <span className="text-danger-default">
                ${applyDiscount(licensePrice)}
              </span>{' '}
              <span className="text-base font-medium text-secondary-300 line-through">
                ${licensePrice}
              </span>
            </h2>
            <p className="overflow-hidden text-ellipsis text-nowrap font-medium">
              / month
            </p>
          </div>
          <DiscountBadge className="xl:max-[1370px]:hidden" />
        </>
      )
    }

    return (
      <>
        <h2 className="text-3xl font-semibold">${licensePrice} </h2>
        <p className="font-medium">/ month</p>
      </>
    )
  }, [licensePrice, showDiscount, applyDiscount])

  const handleLicenseClick = () => {
    setPurchaseOpen(true)
  }

  const getLicenseButton = useMemo(
    () => {
      const button = (
        <Button
          className={classNames('w-full', {
            'bg-danger-default hover:bg-danger-dark': showDiscount,
          })}
          onClick={handleLicenseClick}
          disabled={inTrial}
        >
          Subscribe
        </Button>
      )

      if (inTrial) {
        return (
          <Tooltip
            content="Please end your trial to purchase new licenses."
            className="w-full"
          >
            {button}
          </Tooltip>
        )
      }

      return button
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [inTrial, setPurchaseOpen, showDiscount],
  )

  return (
    <Card className="relative flex flex-1 flex-col justify-between p-6">
      {isRecommend && (
        <div className="absolute right-0 top-0 rounded-bl-lg rounded-tr-lg bg-primary-400/10 p-2 px-3 font-medium dark:bg-primary-400/5">
          <span className="text-primary-400">Recommended</span>
        </div>
      )}
      <CardHeader className="px-0 pt-0">
        <p className="font-medium">{license.displayName}</p>
        <div className="flex items-end gap-2 overflow-hidden align-bottom">
          {price}
        </div>
        {mode === LicenseMode.YEARLY && <p>Billed yearly</p>}
      </CardHeader>
      <CardContent className="flex flex-1 flex-col gap-4 px-0">
        <p className="font-medium">What&apos;s included:</p>
        {license.displayBenefits.map((inc, index) => {
          return (
            <div
              key={inc.substring(0, 5) + index}
              className="grid grid-flow-col items-center justify-start gap-2"
            >
              <CheckIcon className="text-success-default" />
              <p className="text-secondary-300">{inc}</p>
            </div>
          )
        })}
        {license.displayNonBenefits.map((inc, index) => {
          return (
            <div
              key={inc.substring(0, 5) + index}
              className="grid grid-flow-col items-center justify-start gap-2"
            >
              <CloseIcon className="text-danger-default" />
              <p className="text-secondary-300">{inc}</p>
            </div>
          )
        })}
      </CardContent>
      <CardFooter className="flex flex-col gap-3 p-0">
        <a
          href={license.helpDeskLink}
          target="_blank"
          className="w-full"
          rel="noreferrer"
        >
          <Button
            className={classNames('w-full', {
              'border-primary-500 text-primary-500': !showDiscount,
            })}
            variant="outline"
          >
            More Information
          </Button>
        </a>

        {getLicenseButton}
      </CardFooter>
      <PurchaseLicenseDialog
        open={purchaseOpen}
        setOpen={setPurchaseOpen}
        clickedPlan={license}
        defaultToggle={mode}
      />
    </Card>
  )
}
